"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useState, useRef } from "react";

import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getHomeBlurPlaceholder } from "@/utils/homeBlurPlaceholders";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUrl } from "@/utils/languageUtils";
import { Language } from "@/data/translations";

export interface WellnessGalleryItem {
  id: string;
  title: string;
  description: string;
  href: string;
  image: string;
  category:
    | "Wellness"
    | "Medical Spa"
    | "Detox"
    | "Light Therapy"
    | "Massage"
    | "BioScan"
    | "PelviPower"
    | "Nutrition";
}

export interface WellnessGalleryCarouselProps {
  title?: string;
  description?: string;
  items: WellnessGalleryItem[];
}

const wellnessData: WellnessGalleryItem[] = [
  {
    id: "wellness-1",
    title: "Holistic Wellness Center",
    description:
      "Experience comprehensive wellness programs combining medical expertise with traditional healing methods in our serene Alpine setting.",
    href: "/spa",
    image: "/images/Clinique Nutraswiss/IMG-20250812-WA0027.optimized.webp",
    category: "Wellness",
  },
  {
    id: "medical-spa-1",
    title: "Medical Spa Excellence",
    description:
      "State-of-the-art medical spa treatments designed to promote healing, rejuvenation, and optimal health in a luxurious environment.",
    href: "/spa",
    image: "/images/Clinique Nutraswiss/IMG-20250812-WA0072.optimized.webp",
    category: "Medical Spa",
  },
  {
    id: "detox-1",
    title: "Detox & Cleansing",
    description:
      "Comprehensive detox programs to cleanse your body of toxins and restore natural balance through specialized treatments and nutrition.",
    href: "/spa",
    image:
      "/images/Homepage Shortlisted media/ Gallery 03 Clinique Nutraswiss/a_vase_and_a_bowl_of_apples_on_a_table_at_Parkhote_50.optimized.webp",
    category: "Detox",
  },
  {
    id: "relaxation-balcony",
    title: "Relaxation Balcony",
    description:
      "Enjoy peaceful moments on our panoramic balcony with stunning Alpine views, perfect for meditation and quiet reflection.",
    href: "/spa",
    image:
      "/images/Homepage Shortlisted media/ Gallery 03 Clinique Nutraswiss/CS 5panoromic balcony angle 3.high-quality.optimized.webp",
    category: "Wellness",
  },
  {
    id: "massage-1",
    title: "Therapeutic Massage",
    description:
      "Specialized massage treatments including full-body, head, and back massages to provide targeted relaxation and healing.",
    href: "/spa",
    image: "/images/new masseuse.high-quality.optimized.webp",
    category: "Massage",
  },
];

const WellnessGalleryCarousel = ({
  title,
  description,
  items = wellnessData,
}: WellnessGalleryCarouselProps) => {
  // Safe hook usage with fallback
  let t: (key: string) => string;
  let language: Language = "en";
  try {
    const { t: useLanguageT, language: useLanguageLang } = useLanguage();
    t = useLanguageT;
    language = useLanguageLang;
  } catch (error) {
    // Fallback to English translations if context is not available
    t = (key: string) => {
      const fallbackTranslations: Record<string, string> = {
        "spa.title": "Your Wellness Escape",
        "spa.description":
          "Escape to our tranquil Alpine sanctuary where relaxation meets rejuvenation. Experience world-class spa treatments, therapeutic massages, and wellness programs designed to restore your mind, body, and soul in the pristine Swiss mountains.",
        "spa.exploreSpa": "Explore Spa",
        "spa.holisticWellnessTitle": "Holistic Wellness Center",
        "spa.holisticWellnessDesc":
          "Experience comprehensive wellness programs combining medical expertise with traditional healing methods in our serene Alpine setting.",
        "spa.medicalSpaTitle": "Medical Spa Excellence",
        "spa.medicalSpaDesc":
          "State-of-the-art medical spa treatments designed to promote healing, rejuvenation, and optimal health in a luxurious environment.",
        "spa.detoxTitle": "Detox & Cleansing",
        "spa.detoxDesc":
          "Comprehensive detox programs to cleanse your body of toxins and restore natural balance through specialized treatments and nutrition.",
        "spa.relaxationBalconyTitle": "Relaxation Balcony",
        "spa.relaxationBalconyDesc":
          "Enjoy peaceful moments on our panoramic balcony with stunning Alpine views, perfect for meditation and quiet reflection.",
        "spa.therapeuticMassageTitle": "Therapeutic Massage",
        "spa.therapeuticMassageDesc":
          "Specialized massage treatments including full-body, head, and back massages to provide targeted relaxation and healing.",
      };
      return fallbackTranslations[key] || key;
    };
  }
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentSlideGroup, setCurrentSlideGroup] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Helper function to translate wellness titles and descriptions
  const translateWellnessContent = (
    category: string,
    field: "title" | "description",
  ) => {
    const translations: Record<string, Record<string, string>> = {
      Wellness: {
        title: t("spa.holisticWellnessTitle"),
        description: t("spa.holisticWellnessDesc"),
      },
      "Medical Spa": {
        title: t("spa.medicalSpaTitle"),
        description: t("spa.medicalSpaDesc"),
      },
      Detox: {
        title: t("spa.detoxTitle"),
        description: t("spa.detoxDesc"),
      },
      "Light Therapy": {
        title: t("spa.relaxationBalconyTitle"),
        description: t("spa.relaxationBalconyDesc"),
      },
      Massage: {
        title: t("spa.therapeuticMassageTitle"),
        description: t("spa.therapeuticMassageDesc"),
      },
    };

    return (
      translations[category]?.[field] || (field === "title" ? category : "")
    );
  };

  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      const selectedSlide = carouselApi.selectedScrollSnap();
      setCurrentSlide(selectedSlide);
      // Calculate which group of 3 slides is currently visible
      setCurrentSlideGroup(Math.floor(selectedSlide / 3));
    };
    updateSelection();
    carouselApi.on("select", updateSelection);
    return () => {
      carouselApi.off("select", updateSelection);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const container = carouselApi.containerNode();
    if (!container) return;

    let scrollAccumulator = 0;
    const scrollThreshold = 80; // Require more scroll distance before triggering

    const handleWheel = (e: WheelEvent) => {
      // Check if horizontal scroll is intended (shift+wheel or trackpad horizontal gesture)
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) {
        e.preventDefault();

        const scrollAmount = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        scrollAccumulator += scrollAmount;

        if (Math.abs(scrollAccumulator) >= scrollThreshold) {
          if (scrollAccumulator > 0) {
            carouselApi.scrollNext();
          } else {
            carouselApi.scrollPrev();
          }
          scrollAccumulator = 0; // Reset after triggering
        }
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [carouselApi]);

  // Auto progress functionality
  useEffect(() => {
    if (!carouselApi || isHovered) return;

    const interval = setInterval(() => {
      if (carouselApi.canScrollNext()) {
        carouselApi.scrollNext();
      } else {
        carouselApi.scrollTo(0); // Reset to first slide
      }
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [carouselApi, isHovered]);

  // Video visibility detection with 70% threshold
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
            setIsVideoVisible(true);
            video.play().catch(console.error);
          } else {
            setIsVideoVisible(false);
            video.pause();
          }
        });
      },
      {
        threshold: 0.7, // 70% visibility threshold
        rootMargin: "0px",
      },
    );

    observer.observe(video);

    return () => {
      observer.unobserve(video);
    };
  }, []);

  // Safari video autoplay fix - Programmatic play trigger
  // Note: IntersectionObserver handles play/pause based on visibility
  // This ensures Safari can autoplay by attempting play() when metadata loads
  // IntersectionObserver will manage actual playback based on visibility
  useEffect(() => {
    const playVideo = async () => {
      if (!videoRef.current) return;

      try {
        // Always try to play when metadata loads (helps Safari autoplay policy)
        // IntersectionObserver will pause if not visible, which may cause AbortError (expected)
        await videoRef.current.play();
      } catch (error) {
        // Ignore AbortError - IntersectionObserver paused it (expected behavior)
        // Also ignore NotAllowedError - Safari's autoplay policy (IntersectionObserver will handle it)
        if (
          error instanceof DOMException &&
          error.name !== "AbortError" &&
          error.name !== "NotAllowedError"
        ) {
          console.log("Video autoplay prevented:", error);
        }
      }
    };

    // Try when video metadata is loaded (important for Safari autoplay policy)
    const video = videoRef.current;
    if (video) {
      video.addEventListener("loadedmetadata", playVideo);
      video.addEventListener("canplay", playVideo);
      return () => {
        video.removeEventListener("loadedmetadata", playVideo);
        video.removeEventListener("canplay", playVideo);
      };
    }
  }, []);

  return (
    <section
      className="pt-20 pb-4 bg-gradient-to-b from-elegant-white via-pebble-beige to-elegant-white"
      data-oid="yiociwd"
    >
      <div
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        data-oid="_0juj:5"
      >
        {/* Mobile Layout */}
        <div className="lg:hidden" data-oid="sxl.qp2">
          {/* Video Section with Title Overlay */}
          <div className="mb-12" data-oid="0l87bv.">
            <div
              className="relative w-full aspect-[9/16] overflow-hidden rounded-xl"
              data-oid="rs8u:j2"
            >
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                controls={false}
                data-oid="j:afpap"
              >
                <source
                  src="/videos/spa-vert-optimized.mp4"
                  type="video/mp4"
                  data-oid="3x3f8q:"
                />
                Your browser does not support the video tag.
              </video>
              {/* Gradient overlay for better text readability */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/40"
                data-oid="4:18yz0"
              ></div>
              {/* Title Overlay at Top */}
              <div
                className="absolute top-0 left-0 right-0 flex items-start justify-center pt-8 z-10"
                data-oid="rm:0py8"
              >
                <div className="text-center px-4" data-oid="l77kxlb">
                  <h2
                    className="font-playfair text-4xl md:text-5xl text-white uppercase"
                    data-oid="avkep7t"
                  >
                    {title || t("spa.title")}
                  </h2>
                </div>
              </div>
              {/* Explore Spa Button Overlay at Bottom */}
              <div
                className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-8 z-10"
                data-oid="7kuw:g2"
              >
                <a href={getLocalizedUrl("/spa", language)} className="group" data-oid="mmcpu1h">
                  <Button
                    className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full font-lato font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    data-oid="zdd0mys"
                  >
                    {t("spa.exploreSpa")}
                    <ArrowRight
                      className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                      data-oid="r-696yh"
                    />
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Description Only */}
          <div className="mb-8" data-oid="2.n9yly">
            <p
              className="font-lato text-lg text-pine-green/70 leading-relaxed mb-4"
              data-oid="65_lg3-"
            >
              {description}
            </p>
          </div>

          {/* Basic Carousel */}
          <div
            className="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            data-oid="vmx03r-"
          >
            <Carousel
              setApi={setCarouselApi}
              opts={{
                align: "start",
                loop: false,
              }}
              className="w-full"
              data-oid="0ux6af8"
            >
              <CarouselContent data-oid="6g1pby2">
                {items.map((item) => (
                  <CarouselItem
                    key={item.id}
                    className="md:basis-1/2 lg:basis-1/3"
                    data-oid="200a9l6"
                  >
                    <a
                      href={getLocalizedUrl("/spa", language)}
                      className="group rounded-xl"
                      data-oid="fl7p:q6"
                    >
                      <div
                        className="group relative h-64 overflow-hidden rounded-xl"
                        data-oid="ft7d81a"
                      >
                        <OptimizedImage
                          src={item.image}
                          alt={translateWellnessContent(item.category, "title")}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          priority={false}
                          fetchPriority="auto"
                          blurDataURL={getHomeBlurPlaceholder(item.image)}
                          data-oid="fsf9_mm"
                        />

                        {/* Alpine-themed gradient overlay */}
                        <div
                          className="absolute inset-0 h-full bg-gradient-to-t from-pine-green/90 via-pine-green/40 to-transparent mix-blend-multiply"
                          data-oid="7.jljye"
                        />

                        <div
                          className="absolute inset-x-0 bottom-0 flex flex-col items-start p-6 text-elegant-white"
                          data-oid="mycb:4k"
                        >
                          <div
                            className="mb-2 text-xl font-semibold font-playfair"
                            data-oid="dd1.1e:"
                          >
                            {translateWellnessContent(item.category, "title")}
                          </div>
                          <div
                            className="mb-4 font-lato text-elegant-white/90 leading-relaxed transition-all duration-300 group-hover:line-clamp-none line-clamp-2"
                            data-oid="-faf.0b"
                          >
                            {translateWellnessContent(
                              item.category,
                              "description",
                            )}
                          </div>
                          <div
                            className="flex items-center text-sm font-lato font-medium group-hover:text-alpine-gold transition-colors duration-300"
                            data-oid="b4j.nbk"
                          >
                            {t("spa.exploreSpa")}{" "}
                            <ArrowRight
                              className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                              data-oid="gcjuvrx"
                            />
                          </div>
                        </div>
                      </div>
                    </a>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {/* Pagination Dots - Mobile/Tablet Only */}
            <div className="flex justify-center gap-2 mt-4" data-oid="2_4:tl7">
              {Array.from({ length: Math.ceil(items.length / 3) }).map(
                (_, index) => (
                  <button
                    key={index}
                    onClick={() => carouselApi?.scrollTo(index * 3)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      currentSlideGroup === index
                        ? "bg-alpine-gold scale-125"
                        : "bg-gray-300 hover:bg-gray-400"
                    }`}
                    aria-label={`Go to slide group ${index + 1}`}
                    data-oid="t55o4tu"
                  />
                ),
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout - Bento Box Grid with Video on Right */}
        <div className="hidden lg:block" data-oid="d44nz2i">
          {/* Bento Box Grid with Video on Right */}
          <div
            className="grid lg:grid-cols-[1fr_420px] lg:gap-4 lg:items-end"
            data-oid="akez9lv"
          >
            {/* Left Column: Description + Carousel */}
            <div
              className="flex flex-col justify-between h-full"
              data-oid="nryu:-0"
            >
              {/* Title, Description and Features - At Top */}
              <div className="mb-3 pl-6 pr-16" data-oid="y-2khb6">
                <h2
                  className="font-playfair text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4 uppercase"
                  data-oid="5y0n4.r"
                >
                  {title || t("spa.title")}
                </h2>
                <p
                  className="font-lato text-lg text-gray-900 leading-relaxed mb-4"
                  data-oid="1vhjqdx"
                >
                  {description || t("spa.description")}
                </p>

                {/* Clinique Nutraswiss Logo */}
                <div className="mt-1 flex justify-center" data-oid="wn05-hz">
                  <img
                    src="/images/clinique_nutraswiss.avif"
                    alt="Clinique Nutraswiss Logo"
                    className="h-[120px] w-auto opacity-80 hover:opacity-100 transition-opacity duration-300"
                    data-oid="d65vps5"
                  />
                </div>
              </div>

              {/* Pagination Controls - Between Text and Carousel */}
              <div className="mb-3 flex gap-2" data-oid="mc7h_gg">
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => carouselApi?.scrollPrev()}
                  disabled={!canScrollPrev}
                  className="border border-gray-300 hover:border-gray-400 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  data-oid="i5jv5vj"
                >
                  <ArrowLeft className="h-4 w-4" data-oid="4b9-ka3" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => carouselApi?.scrollNext()}
                  disabled={!canScrollNext}
                  className="border border-gray-300 hover:border-gray-400 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  data-oid="at1_1l."
                >
                  <ArrowRight className="h-4 w-4" data-oid="69ratd-" />
                </Button>
              </div>

              {/* Carousel - Fixed height for uniform cards */}
              <div
                className="relative h-[400px]"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                data-oid="1792cmu"
              >
                <Carousel
                  setApi={setCarouselApi}
                  opts={{
                    align: "start",
                    loop: false,
                  }}
                  className="w-full h-full"
                  data-oid="jpo.c6j"
                >
                  <CarouselContent className="h-full" data-oid="_:g:s47">
                    {items.map((item) => (
                      <CarouselItem
                        key={item.id}
                        className="basis-1/3 h-[400px]"
                        data-oid="1iqsfet"
                      >
                        <a
                          href={getLocalizedUrl("/spa", language)}
                          className="group rounded-xl"
                          data-oid="y2qqkxy"
                        >
                          <div
                            className="group relative h-[400px] w-full overflow-hidden rounded-xl"
                            data-oid="q6wbfig"
                          >
                            <OptimizedImage
                              src={item.image}
                              alt={translateWellnessContent(
                                item.category,
                                "title",
                              )}
                              className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                              priority={false}
                              data-oid="_wco-z5"
                            />

                            {/* Alpine-themed gradient overlay */}
                            <div
                              className="absolute inset-0 bg-gradient-to-t from-pine-green/90 via-pine-green/40 to-transparent mix-blend-multiply"
                              data-oid="ykpw6nz"
                            />

                            {/* Title at top */}
                            <div
                              className="absolute top-4 left-4 right-4"
                              data-oid="t1sr.2g"
                            >
                              <div
                                className="text-xl font-semibold font-playfair text-elegant-white"
                                data-oid="p:xp:ai"
                              >
                                {translateWellnessContent(
                                  item.category,
                                  "title",
                                )}
                              </div>
                            </div>

                            <div
                              className="absolute inset-x-0 bottom-0 flex flex-col items-start p-6 text-elegant-white"
                              data-oid="yy_vj.e"
                            >
                              <div
                                className="mb-1 group-hover:mb-4 font-lato text-elegant-white/90 leading-relaxed transition-all duration-300 group-hover:line-clamp-none line-clamp-2"
                                data-oid="ksib60-"
                              >
                                {translateWellnessContent(
                                  item.category,
                                  "description",
                                )}
                              </div>
                              <div
                                className="flex items-center text-sm font-lato font-medium group-hover:text-alpine-gold transition-all duration-300 opacity-0 group-hover:opacity-100 h-0 group-hover:h-auto overflow-hidden"
                                data-oid="xb58jyl"
                              >
                                {t("spa.exploreSpa")}{" "}
                                <ArrowRight
                                  className="ml-2 size-4 transition-transform group-hover:translate-x-1"
                                  data-oid="63aogmj"
                                />
                              </div>
                            </div>
                          </div>
                        </a>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              </div>
            </div>

            {/* Right Column: Video */}
            <div
              className="relative w-full aspect-[9/16] overflow-hidden rounded-xl"
              data-oid="xn4xvhc"
            >
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                controls={false}
                data-oid="wobdi-0"
              >
                <source
                  src="/videos/spa-vert-optimized.mp4"
                  type="video/mp4"
                  data-oid="qfie29u"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { WellnessGalleryCarousel };
